import axios from '@apis/spring-client';
import { Toast } from '@components/_atoms/Toast';
import { HOUR, MINUTE, MONTH } from '@constants/time';
import { ACCESS_TOKEN, REFRESH_TOKEN, setCookie, setSecureCookie } from '@utils/cookie';
import { AxiosError } from 'axios';
import { ErrorResponseObject, Message } from 'models/swagger-spring/data-contracts';
import { useMutation } from 'react-query';
import { publicEncrypt, constants } from 'crypto';

export interface TokenResponse {
  accessToken: string;
  refreshToken: string;
}

export interface SuccessResponseTokenResponse {
  success: boolean;
  /** @format int32 */
  code: number;
  message: Message;
  data: TokenResponse;
}

type LoginRequest = {
  email: string;
  password: string;
};

const login = async (loginRequest: LoginRequest): Promise<SuccessResponseTokenResponse> => {
  try {
    const response = await axios.post(`/api/auth/login`, loginRequest);
    return response.data;
  } catch (error) {
    throw error;
  }
};

const base64PublicKey = process.env.NEXT_PUBLIC_RSA_PUBLIC_KEY ?? '';
const publicKey = `-----BEGIN PUBLIC KEY-----\n${base64PublicKey}\n-----END PUBLIC KEY-----`;

export const useLogin = () => {
  return useMutation(
    ['login'],
    ({ email, password }: LoginRequest) =>
      login({
        email,
        password: publicEncrypt(
          {
            key: publicKey,
            padding: constants.RSA_PKCS1_PADDING,
          },
          Buffer.from(password),
        ).toString('base64'),
      }),
    {
      onSuccess: (response) => {
        const currentDomain = window.location.hostname;

        setCookie(ACCESS_TOKEN, response.data.accessToken, { maxAge: 1 * HOUR, domain: currentDomain, path: '/' });
        setCookie(REFRESH_TOKEN, response.data.refreshToken, { maxAge: 3 * MONTH, domain: currentDomain, path: '/' });
      },
      onError: (error: AxiosError<ErrorResponseObject>) => {
        Toast.error(error.response?.data.message.text);
      },
    },
  );
};

export default useLogin;
